// Cliente HTTP para la API de Camadero.
//
// - Añade el header `Authorization: Bearer <token>` automáticamente.
// - Cuando recibe 401 intenta refrescar el token con el refresh_token
//   guardado en localStorage y reintenta la petición una vez.
// - Si el refresh es inválido, limpia tokens y dispara 'auth:logout'.
//   Los fallos de red o del servidor conservan la sesión para reintentar.

import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  useAuthStore,
} from '@/stores/auth';
import {
  handleOfflineRequest,
  checkCurrentReadPermissions,
  checkReadGeneration,
} from './offline/operations';
import { cacheGeneration, currentOwner } from './offline/cache';
import type { OfflineBundle } from './offline/domainTypes';

export type ApiRequestOptions = RequestInit & {
  auth?: boolean;
  expectedOwnerId?: number;
  offlineBase?: OfflineBundle | null;
};

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Bound the whole request, including its body, even if transport ignores abort. */
async function boundedRequest<T>(
  callerSignal: AbortSignal | null | undefined,
  request: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  let rejectRequest!: (reason: unknown) => void;
  const cancelled = new Promise<never>((_, reject) => {
    rejectRequest = reject;
  });
  const abort = () => {
    const error = new DOMException('Petición cancelada.', 'AbortError');
    controller.abort(error);
    rejectRequest(error);
  };
  const timer = window.setTimeout(() => {
    const error = new TypeError('La conexión no responde. Reintenta cuando tengas conexión.');
    controller.abort(error);
    rejectRequest(error);
  }, 10_000);
  callerSignal?.addEventListener('abort', abort, { once: true });
  if (callerSignal?.aborted) abort();
  try {
    return await Promise.race([
      cancelled,
      Promise.resolve().then(() => {
        controller.signal.throwIfAborted();
        return request(controller.signal);
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
    callerSignal?.removeEventListener('abort', abort);
  }
}

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

/** Association check only. The API verifies the JWT signature. */
export function tokenOwner(token: string | null): number | null {
  try {
    if (!token) return null;
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const subject = JSON.parse(atob(encoded)).sub;
    const owner = Number(subject);
    return Number.isSafeInteger(owner) && owner > 0 ? owner : null;
  } catch {
    return null;
  }
}
export function storedOwner(): number | null {
  try {
    return JSON.parse(localStorage.getItem('camadero.user') ?? 'null')?.id ?? null;
  } catch {
    return null;
  }
}

function makeError(message: string, status: number, body: unknown): ApiError {
  const err = new Error(message) as ApiError;
  err.status = status;
  err.body = body;
  return err;
}

/**
 * Extrae un mensaje legible de un error de la API para mostrar al
 * usuario en toasts/banners.
 *
 * El backend de Camadero responde con `detail` en uno de estos formatos:
 *   - string simple: `{ detail: "Ya existe..." }`
 *   - objeto estructurado: `{ detail: { code: "multiple_camadas_activas",
 *                                     message: "Ya existe..." } }`
 *   - array de validación FastAPI: `{ detail: [{ loc: [...], msg: "..." }] }`
 *
 * Si ninguno encaja, devuelve el `message` del Error nativo o el fallback.
 *
 * REGLA de UX: el usuario SIEMPRE debe saber por qué falló la acción.
 * NUNCA mostrar "[object Object]" (eso pasa cuando renderizas un
 * objeto `{...}` directamente en JSX).
 */
export function extractApiMessage(err: unknown, fallback = 'Error desconocido'): string {
  if (err && typeof err === 'object') {
    const e = err as ApiError;
    const body = e.body;
    if (body && typeof body === 'object') {
      const detail = (body as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail) return detail;
      if (detail && typeof detail === 'object') {
        // Objeto estructurado {code, message}.
        const message = (detail as { message?: unknown }).message;
        if (typeof message === 'string' && message) return message;
        // FastAPI 422: detail es un array.
        if (Array.isArray(detail) && detail.length > 0) {
          const first = detail[0] as { msg?: unknown; loc?: unknown };
          const msg = typeof first?.msg === 'string' ? first.msg : null;
          const loc = Array.isArray(first?.loc) ? first.loc.join('.') : null;
          if (msg && loc) return `${msg} (${loc})`;
          if (msg) return msg;
        }
      }
    }
    if (typeof e.message === 'string' && e.message) return e.message;
  }
  return fallback;
}

export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const local = await handleOfflineRequest<T>(path, options);
  if (local.handled) return local.value as T;
  const generation = await cacheGeneration(currentOwner()).catch(() => undefined);
  const result = await apiFetchRaw<T>(path, options);
  await checkCurrentReadPermissions(path, options);
  await checkReadGeneration(path, options, generation).catch((error) => {
    if ((error as { status?: number })?.status) throw error;
  });
  return result;
}

export async function apiFetchRaw<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return boundedRequest(options.signal, (signal) => requestRaw<T>(path, { ...options, signal }));
}

async function requestRaw<T>(path: string, options: ApiRequestOptions): Promise<T> {
  const { auth = true, expectedOwnerId, offlineBase: _offlineBase, ...init } = options;
  const access = auth ? getAccessToken() : null;
  const refresh = auth ? getRefreshToken() : null;
  const requestOwner =
    expectedOwnerId ??
    (auth && path !== '/v1/auth/me' && tokenOwner(access) !== null
      ? (storedOwner() ?? undefined)
      : undefined);
  const url = `${API_BASE}${path}`;

  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const assertOwner = (token: string | null) => {
    if (
      requestOwner !== undefined &&
      (storedOwner() !== requestOwner ||
        useAuthStore.getState().user?.id !== requestOwner ||
        tokenOwner(token) !== requestOwner)
    ) {
      throw makeError('La sesión ha cambiado. Pendiente de sincronizar con su usuario.', 401, null);
    }
  };
  const doRequest = (token: string | null) => {
    assertOwner(token);
    if (auth && token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return fetch(url, { ...init, headers });
  };

  let requestToken = access;
  let response = await doRequest(requestToken);
  init.signal?.throwIfAborted();
  assertOwner(requestToken);

  if (response.status === 401 && auth && refresh) {
    // Intentar refresh una vez.
    const refreshed = await tryRefresh(access, refresh);
    init.signal?.throwIfAborted();
    if (refreshed) {
      requestToken = refreshed.access_token;
      response = await doRequest(requestToken);
      init.signal?.throwIfAborted();
      assertOwner(requestToken);
    } else {
      if (getAccessToken() !== access || getRefreshToken() !== refresh)
        throw makeError('La sesión ha cambiado.', 401, null);
      clearTokens();
      window.dispatchEvent(new CustomEvent('auth:logout'));
      throw makeError('Sesión expirada', 401, null);
    }
  }

  const text = await response.text();
  init.signal?.throwIfAborted();
  assertOwner(requestToken);
  const body = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    const detail = (body as { detail?: string } | null)?.detail ?? response.statusText;
    throw makeError(detail, response.status, body);
  }
  return body as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

interface Tokens {
  access_token: string;
  refresh_token: string;
}
const refreshing = new Map<string, Promise<Tokens | null>>();

async function tryRefresh(access: string | null, refresh: string): Promise<Tokens | null> {
  // Deduplicación: si ya hay un refresh en curso, esperamos ese.
  const existing = refreshing.get(refresh);
  if (existing) return existing;

  const request = boundedRequest(undefined, async (signal) => {
    const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
      signal,
    });
    signal.throwIfAborted();
    if (!res.ok) {
      if (res.status === 400 || res.status === 401 || res.status === 403) return null;
      throw makeError('No se pudo renovar la sesión. Reintenta con conexión.', res.status, null);
    }
    const body = await res.json();
    signal.throwIfAborted();
    if (getAccessToken() !== access || getRefreshToken() !== refresh)
      throw makeError('La sesión ha cambiado.', 401, null);
    setTokens(body.access_token, body.refresh_token);
    return body as Tokens;
  }).finally(() => refreshing.delete(refresh));

  refreshing.set(refresh, request);
  return request;
}
